import { api } from '@web/api/client'
import type {
  AppDefinition,
  BridgeConfig,
  SessionHistoryItem,
  SessionRecord,
} from '@web/api/types'
import { SessionAvatar } from '@web/components/mobile/MobileHome'
import { Row, RowGroup, Sheet } from '@web/components/mobile/Sheet'
import { groupSessions } from '@web/components/mobile/sessions'
import { CircleButton, TopBar } from '@web/components/mobile/TopBar'
import { RenameSession } from '@web/components/RenameSession'
import { Icon } from '@web/components/ui/icons'
import { basename, fmtRelative, modelLabel } from '@web/lib/format'
import {
  navigateWorkspace as navigate,
  navigateBack,
  useIdentity,
} from '@web/state/identity'
import { ACTIVE_STATUSES } from '@web/state/session-order'
import { dropSessionStore } from '@web/state/session-store'
import { useMemo, useState } from 'react'

/** The sidebar's session history as a phone screen: cards grouped by activity and project. */
export function SessionsScreen({
  apps,
  sessions,
  appId,
  config,
  refreshing,
  error,
  hasMoreSessions,
  loadingMoreSessions,
  onLoadMoreSessions,
  onRenamed,
  onChanged,
  archived,
  onToggleArchived,
}: {
  apps: AppDefinition[]
  sessions: SessionHistoryItem[]
  appId?: string
  config: BridgeConfig | null
  refreshing: boolean
  error: string
  hasMoreSessions: boolean
  loadingMoreSessions: boolean
  onLoadMoreSessions: () => Promise<void>
  onRenamed: (record: SessionRecord) => void
  onChanged: () => Promise<void>
  archived: boolean
  onToggleArchived: () => void
}) {
  const identity = useIdentity()
  const app = apps.find((item) => item.id === appId)
  const [menu, setMenu] = useState<SessionHistoryItem | null>(null)
  const [renaming, setRenaming] = useState<SessionHistoryItem | null>(null)
  const [failure, setFailure] = useState('')
  // Archiving waits for the sandbox to wind down; hide the row at once so the
  // list does not shift under a second tap while the request is in flight.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const groups = useMemo(
    () =>
      groupSessions(
        sessions.filter(
          (session) =>
            (session.status === 'archived') === archived &&
            !hidden.has(session.id),
        ),
        app?.id,
      ),
    [sessions, app?.id, archived, hidden],
  )
  const active = groups
    .filter((group) => group.active)
    .reduce((sum, group) => sum + group.items.length, 0)
  const past = groups
    .filter((group) => !group.active)
    .reduce((sum, group) => sum + group.items.length, 0)
  const run = async (action: () => Promise<unknown>) => {
    setFailure('')
    try {
      await action()
      await onChanged()
    } catch (problem) {
      setFailure((problem as Error).message)
    }
  }
  const canStart =
    app &&
    app.launchMode !== 'overview' &&
    app.launchMode !== 'direct' &&
    app.descriptor?.configuration?.sessions.nature !== 'subagent'
  return (
    <main className="m-screen relative" aria-label="Sessions">
      <TopBar
        title={app ? `${app.title} sessions` : 'Sessions'}
        subtitle={
          error
            ? 'Session updates unavailable. Retrying automatically.'
            : refreshing
              ? 'Updating…'
              : app
                ? app.description
                : `${active} active · ${past} past`
        }
        onBack={() => navigateBack(app ? `/apps/${app.id}` : '/')}
      />
      <div className="m-scroll" style={{ gap: 10, paddingBottom: 110 }}>
        {failure && (
          <p role="alert" className="m-error">
            {failure}
          </p>
        )}
        {groups.length === 0 && (
          <div className="m-empty">
            <span>
              {archived ? 'No archived sessions.' : 'No sessions here yet.'}
            </span>
            <small>
              {archived
                ? 'Sessions you archive appear here.'
                : app
                  ? 'Start a new session to see it here.'
                  : 'Choose an agent to begin.'}
            </small>
          </div>
        )}
        {groups.map(({ key, section, project, items, showProject }) => (
          <div key={key} className="m-list">
            {section && (
              <div className="m-list-section">
                <span className="label">
                  {archived ? 'Archived sessions' : section}
                </span>
              </div>
            )}
            {showProject && (
              <div className="m-list-project" title={project}>
                <Icon name="folder" size={13} className="text-faint" />
                <span className="truncate">{basename(project) || project}</span>
              </div>
            )}
            {items.map((session) => {
              const catalogApp = apps.find((item) => item.id === session.app_id)
              const mine =
                session.owner_user_id === identity.state?.current_user?.id
              return (
                <div key={session.id} className="m-card-row">
                  <button
                    type="button"
                    className="m-card-row-main"
                    aria-label={`Open session ${session.title}`}
                    onClick={() =>
                      navigate(
                        `/apps/${session.app_id}/sessions/${session.id}${archived ? '?archived=1' : ''}`,
                      )
                    }
                  >
                    <SessionAvatar
                      size={38}
                      src={
                        session.config.package_digest
                          ? `/v1/apps/${session.app_id}/icon?packageDigest=${encodeURIComponent(session.config.package_digest)}`
                          : (catalogApp?.iconUrl ??
                            `/v1/apps/${encodeURIComponent(session.app_id)}/icon`)
                      }
                      title={catalogApp?.title ?? session.app_id}
                      status={session.status}
                    />
                    <span className="m-recent-copy">
                      <span
                        className={`m-recent-title ${session.title_provisional ? 'provisional-title' : ''}`}
                      >
                        {session.title}
                        {!mine && ' · Shared'}
                      </span>
                      <span className="m-recent-meta">
                        {[
                          app ? null : (catalogApp?.title ?? session.app_id),
                          session.model || session.config.model
                            ? modelLabel(
                                session.model ?? session.config.model,
                                config?.models ?? [],
                              )
                            : null,
                          fmtRelative(session.updated_at),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </button>
                  {mine && (
                    <CircleButton
                      icon="more"
                      tone="ghost"
                      label={`Session options: ${session.title}`}
                      onClick={() => setMenu(session)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ))}
        {hasMoreSessions && (
          <button
            type="button"
            className="text-left text-xs text-muted py-2 disabled:opacity-50"
            onClick={() => void onLoadMoreSessions()}
            disabled={loadingMoreSessions}
          >
            {loadingMoreSessions
              ? 'Loading older sessions…'
              : 'Load older sessions'}
          </button>
        )}
        <button
          type="button"
          className="text-left text-xs text-muted py-2"
          onClick={onToggleArchived}
        >
          {archived ? 'Back to sessions' : 'Archived sessions'}
        </button>
      </div>
      {canStart && (
        <div className="m-fab-holder">
          <button
            type="button"
            className="m-fab"
            onClick={() =>
              navigate(
                `/apps/${app.id}${app.id === 'chronicle' ? `?new=${crypto.randomUUID()}` : ''}`,
              )
            }
          >
            <Icon name="plus" size={15} />
            {app.descriptor?.configuration?.sessions.nature === 'implicit'
              ? 'Open workspace'
              : 'New session'}
          </button>
        </div>
      )}
      <Sheet
        open={!!menu}
        onClose={() => setMenu(null)}
        title={menu?.title ?? 'Session options'}
      >
        <RowGroup>
          <Row
            icon="edit"
            label="Rename session"
            description="Keeps your name; automatic naming stops."
            onClick={() => {
              const target = menu
              setMenu(null)
              setRenaming(target)
            }}
          />
          {menu &&
            ACTIVE_STATUSES.has(menu.status) &&
            menu.status !== 'created' && (
              <Row
                icon="stop"
                label="Stop session"
                description="Ends the agent; the transcript stays."
                onClick={() => {
                  const target = menu
                  setMenu(null)
                  void run(() => api.stopSession(target.id))
                }}
              />
            )}
          {menu?.status !== 'archived' && (
            <Row
              icon="archive"
              label="Archive session"
              description="Keeps the transcript; hides it from the list."
              onClick={() => {
                const target = menu
                setMenu(null)
                if (!target) return
                setHidden((current) => new Set(current).add(target.id))
                void run(async () => {
                  try {
                    await api.archiveSession(target.id)
                    dropSessionStore(target.id)
                  } finally {
                    setHidden((current) => {
                      const next = new Set(current)
                      next.delete(target.id)
                      return next
                    })
                  }
                })
              }}
            />
          )}
        </RowGroup>
      </Sheet>
      {renaming && (
        <RenameSession
          session={renaming}
          onClose={() => setRenaming(null)}
          onSaved={onRenamed}
        />
      )}
    </main>
  )
}
